import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  List,
  ListItem,
  ListItemText,
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useColors } from 'legacyfrontend/hooks/useColors';

const Transition = React.forwardRef((props, ref) => {
  return <Slide direction='up' ref={ref} {...props} />;
});

const HotelDestinationDialog = ({
  options = [],
  onSelect,
  selectedDestination,
  isOpen,
  setIsOpen,
  isDestinationEmpty,
  setIsDestinationEmpty,
  handleDestinationSearch,
  isLoading = false,
}) => {
  const [localSearchValue, setLocalSearchValue] = useState('');
  const [optionTapped, setOptionTapped] = useState(undefined);
  const { background } = useColors();

  const filteredOptions = useMemo(() => {
    if (!localSearchValue.trim()) return options;

    const searchLower = localSearchValue.toLowerCase();
    return options.filter(
      (option) =>
        option.cityName?.toLowerCase().includes(searchLower) ||
        option.name?.toLowerCase().includes(searchLower) ||
        option.countryName?.toLowerCase().includes(searchLower)
    );
  }, [options, localSearchValue]);

  const groupedOptions = useMemo(() => {
    return filteredOptions.reduce((groups, option) => {
      const country = option.countryName || 'Otros';
      if (!groups[country]) {
        groups[country] = [];
      }
      groups[country].push(option);
      return groups;
    }, {});
  }, [filteredOptions]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsDestinationEmpty(false);
  }, [setIsOpen, setIsDestinationEmpty]);

  const handleOptionSelect = useCallback(
    (option) => {
      setOptionTapped(option);
      onSelect(option);
      setIsOpen(false);
      setIsDestinationEmpty(false);
    },
    [onSelect, setIsOpen, setIsDestinationEmpty]
  );

  useEffect(() => {
    if (isOpen) {
      setLocalSearchValue('');
      setOptionTapped(selectedDestination);
    }
  }, [isOpen, selectedDestination]);

  const styles = {
    groupHeader: {
      position: 'sticky',
      top: 0,
      padding: '12px 16px',
      backgroundColor: '#fafafa',
      fontWeight: 600,
      fontSize: '14px',
      color: '#000',
      zIndex: 1,
      borderBottom: '1px solid #eee',
    },
    listItem: {
      padding: '12px 16px',
      borderBottom: '1px solid #f0f0f0',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
    selectedItem: {
      backgroundColor: '#e3f2fd',
    },
  };

  return (
    <Dialog
      TransitionComponent={Transition}
      fullScreen
      open={isOpen}
      onClose={handleClose}
      slotProps={{
        backdrop: {
          style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
        },
      }}
    >
      <AppBar sx={{ position: 'relative', backgroundColor: background }}>
        <Toolbar>
          <IconButton edge='start' color='inherit' onClick={handleClose} aria-label='close'>
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant='h6' component='div'>
            Seleccionar Destino
          </Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(100vh - 64px)' }}>
        <TextField
          fullWidth
          autoFocus
          placeholder='Buscar destino...'
          value={localSearchValue}
          onChange={(e) => {
            setLocalSearchValue(e.target.value);
            if (handleDestinationSearch) {
              handleDestinationSearch(e.target.value);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ padding: 0 }}>
            {Object.entries(groupedOptions).map(([country, countryOptions]) => (
              <Box key={country}>
                <Typography sx={styles.groupHeader}>{country}</Typography>
                {countryOptions.map((option, index) => (
                  <ListItem
                    key={option.id || `${country}-${index}`}
                    onClick={() => handleOptionSelect(option)}
                    sx={{
                      ...styles.listItem,
                      ...(optionTapped && option.id === optionTapped.id && styles.selectedItem),
                    }}
                  >
                    <ListItemText primary={option.cityName || option.name} />
                  </ListItem>
                ))}
              </Box>
            ))}
          </List>
        )}
      </Box>
    </Dialog>
  );
};

export default HotelDestinationDialog;
